// Focus trap utilitário compartilhado entre dialog/sheet/command/alert-dialog.
//
// Uso típico no Stimulus controller:
//
//   import { activateFocusTrap, deactivateFocusTrap } from "./utils/focus_trap"
//
//   open() {
//     this.previousFocus = document.activeElement
//     this.trap = activateFocusTrap(this.rootTarget)
//   }
//
//   close() {
//     deactivateFocusTrap(this.trap)
//     this.previousFocus?.focus?.()  // restaura foco ao trigger
//   }
//
// O trap captura Tab/Shift+Tab dentro do container, foca o primeiro elemento
// focável ao abrir, e libera no close. NÃO captura Esc — quem usa decide
// (controllers já têm listeners de Esc próprios).
//
// `inert` é aplicado nos siblings do container (em vez do <body>) — o
// container fica clicável enquanto o resto da página fica inacessível.

const FOCUSABLE = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable=false])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

export function activateFocusTrap(container) {
  if (!container) return null

  const previousFocus = document.activeElement
  const siblings      = getInertSiblings(container)
  siblings.forEach((el) => el.setAttribute("inert", ""))

  const handleKeydown = (e) => {
    if (e.key !== "Tab") return
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE))
      .filter((el) => el.offsetParent !== null || el === document.activeElement)
    if (focusable.length === 0) {
      e.preventDefault()
      return
    }
    const first = focusable[0]
    const last  = focusable[focusable.length - 1]

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  container.addEventListener("keydown", handleKeydown)

  // Foca o primeiro focável (ou o próprio container se nada estiver dentro).
  // Usa requestAnimationFrame pra esperar o display:flex/block ser aplicado.
  requestAnimationFrame(() => {
    const focusables = container.querySelectorAll(FOCUSABLE)
    const target = focusables[0] || container
    target.focus?.()
  })

  return { container, siblings, handleKeydown, previousFocus }
}

export function deactivateFocusTrap(trap) {
  if (!trap) return
  trap.container.removeEventListener("keydown", trap.handleKeydown)
  trap.siblings.forEach((el) => el.removeAttribute("inert"))
  trap.previousFocus?.focus?.()
}

// Retorna todos os elementos no <body> que NÃO contêm o container (sibling tree).
// Aplicar `inert` neles isola o modal sem mexer no <body> inteiro.
function getInertSiblings(container) {
  const result = []
  const walk = (node) => {
    Array.from(node.children).forEach((child) => {
      if (child === container || child.contains(container)) {
        if (child !== container) walk(child)
        return
      }
      if (child.tagName === "SCRIPT" || child.tagName === "STYLE") return
      result.push(child)
    })
  }
  walk(document.body)
  return result
}
